import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { ArrowRight, FileText, Calendar, Shield, MessageSquare, Brain, ListChecks, GitBranch, Target, Mail, BarChart3, Check } from "lucide-react";

const features = [
  { icon: FileText, title: "Justification traçable", desc: "Chaque short-list est justifiée avec citations exactes du CV. Explication prête à partager." },
  { icon: Calendar, title: "Plan d'entretien clé-en-main", desc: "Guide, questions, relances et grille de scoring générés en 1 clic." },
  { icon: ListChecks, title: "Prochaines actions auto.", desc: "Chaque candidat se transforme en relance, call, ou test à planifier." },
  { icon: GitBranch, title: "CV → profil métier", desc: "CV hétérogènes normalisés en compétences avec niveaux et preuves." },
  { icon: Shield, title: "Triage anti-erreurs", desc: "Détection automatique des trous, incohérences de dates et mismatch titre/exp." },
  { icon: Target, title: "Matching barrières", desc: "Filtres must-have absolus avec motif précis pour chaque inclusion ou exclusion." },
  { icon: MessageSquare, title: "Messages RH non robotisés", desc: "Réponses candidats personnalisées au profil et au stade du process." },
  { icon: Calendar, title: "Calendrier intelligent", desc: "Créneaux suggérés, ordre d'entretien et contenu à préparer pour chaque échange." },
  { icon: BarChart3, title: "Post-mortem qui apprend", desc: "Analyse des recrutements réussis/ratés et mise à jour des règles de scoring." },
  { icon: Brain, title: "Copilote de décision", desc: "Recommandation go/no-go/backup avec note interne prête à coller." },
];

export default function Landing() {
  return (
    <div className="min-h-screen bg-white text-slate-900">
      {/* Nav */}
      <header className="border-b border-slate-200">
        <div className="max-w-7xl mx-auto px-6 lg:px-12 py-5 flex items-center justify-between">
          <Link to="/" className="font-display text-2xl tracking-tight" data-testid="landing-logo">Xpert Recruit AI</Link>
          <nav className="hidden md:flex items-center gap-8 text-sm text-slate-600">
            <a href="#features" className="link-underline">Fonctionnalités</a>
            <a href="#pricing" className="link-underline">Tarifs</a>
            <Link to="/login" className="link-underline" data-testid="landing-login-link">Connexion</Link>
          </nav>
          <Link to="/signup" data-testid="landing-cta-signup">
            <Button className="bg-[#0F172A] hover:bg-[#1E293B] text-white btn-pill px-6">Essayer gratuit</Button>
          </Link>
        </div>
      </header>

      {/* Hero */}
      <section className="relative overflow-hidden">
        <div className="absolute inset-0 bg-grid opacity-50" aria-hidden="true" />
        <div className="max-w-7xl mx-auto px-6 lg:px-12 py-24 lg:py-32 relative">
          <div className="grid lg:grid-cols-12 gap-12 items-start">
            <div className="lg:col-span-7">
              <div className="text-xs tracking-[0.3em] uppercase text-slate-500 mb-6">Recrutement augmenté · 2026</div>
              <h1 className="font-display text-5xl sm:text-6xl lg:text-7xl tracking-tight leading-[0.95]">
                Le copilote IA<br />qui <span className="text-[#2563EB]">justifie</span><br />chaque short-list.
              </h1>
              <p className="text-lg text-slate-600 mt-8 max-w-xl leading-relaxed">
                Plus qu'un score : un livrable RH actionnable. Fiche candidat normalisée, plan d'entretien complet, et justification citant explicitement le CV.
              </p>
              <div className="flex flex-wrap gap-4 mt-10">
                <Link to="/signup" data-testid="hero-cta-signup">
                  <Button size="lg" className="bg-[#2563EB] hover:bg-[#1D4ED8] text-white btn-pill px-8 h-12">
                    Commencer · 7 jours offerts <ArrowRight className="ml-2 w-4 h-4" />
                  </Button>
                </Link>
                <a href="#features">
                  <Button size="lg" variant="outline" className="btn-pill px-8 h-12 border-slate-300">
                    Voir les fonctionnalités
                  </Button>
                </a>
              </div>
              <div className="mt-12 flex items-center gap-6 text-sm text-slate-500">
                <div>✓ Sans CB</div>
                <div>✓ Annulable à tout moment</div>
                <div>✓ 50$/mois après essai</div>
              </div>
            </div>

            <div className="lg:col-span-5">
              <div className="relative">
                <div className="absolute inset-0 bg-[#0F172A] translate-x-3 translate-y-3" aria-hidden="true" />
                <div className="relative bg-white border border-slate-300 p-6">
                  <div className="text-[11px] tracking-[0.2em] uppercase text-slate-400 mb-3">Justification générée</div>
                  <div className="space-y-3 text-sm">
                    <div className="flex items-start gap-2">
                      <Check className="w-4 h-4 text-emerald-600 mt-0.5 shrink-0" />
                      <div>
                        <div className="font-medium">Maîtrise Python avancée</div>
                        <div className="text-slate-500 text-xs mt-1 italic">"Lead Python · 6 ans · architecture micro-services chez Atos"</div>
                      </div>
                    </div>
                    <div className="flex items-start gap-2">
                      <Check className="w-4 h-4 text-emerald-600 mt-0.5 shrink-0" />
                      <div>
                        <div className="font-medium">Expérience SaaS B2B</div>
                        <div className="text-slate-500 text-xs mt-1 italic">"3 ans chez Doctolib en équipe produit B2B"</div>
                      </div>
                    </div>
                    <div className="flex items-start gap-2">
                      <Shield className="w-4 h-4 text-amber-600 mt-0.5 shrink-0" />
                      <div>
                        <div className="font-medium">Gap 8 mois en 2023</div>
                        <div className="text-slate-500 text-xs mt-1 italic">À investiguer en entretien</div>
                      </div>
                    </div>
                  </div>
                  <div className="mt-5 pt-4 border-t border-slate-200 flex items-center justify-between">
                    <div className="text-xs text-slate-500">Score de match</div>
                    <div className="font-mono text-2xl font-bold">87<span className="text-base text-slate-400">/100</span></div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Trust strip */}
      <section className="border-y border-slate-200 bg-[#F8FAFC]">
        <div className="max-w-7xl mx-auto px-6 lg:px-12 py-8 flex flex-wrap items-center justify-between gap-6">
          <div className="text-sm text-slate-500 tracking-wide">Conçu pour les recruteurs qui doivent <span className="text-slate-900 font-medium">pouvoir expliquer</span> leurs décisions.</div>
          <div className="flex items-center gap-8 text-slate-400 text-xs font-medium tracking-widest uppercase">
            <span>RH augmenté</span><span>·</span><span>Traçabilité</span><span>·</span><span>Conformité</span><span>·</span><span>RGPD-ready</span>
          </div>
        </div>
      </section>

      {/* Features */}
      <section id="features" className="py-24 lg:py-32">
        <div className="max-w-7xl mx-auto px-6 lg:px-12">
          <div className="grid lg:grid-cols-12 gap-12 mb-16">
            <div className="lg:col-span-5">
              <div className="text-xs tracking-[0.3em] uppercase text-slate-500 mb-4">10 fonctionnalités</div>
              <h2 className="font-display text-4xl sm:text-5xl tracking-tight">Tout ce qu'il faut<br />pour passer du <span className="text-slate-400">score</span><br />au <span className="text-[#2563EB]">livrable</span>.</h2>
            </div>
            <div className="lg:col-span-7 text-slate-600 text-lg leading-relaxed lg:pt-16">
              La plupart des outils s'arrêtent à un score opaque. <span className="text-slate-900 font-medium">Xpert Recruit AI</span> va plus loin : pack d'entretien complet, fiche candidat standardisée, et justification qui cite explicitement les éléments du CV.
            </div>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-px bg-slate-200 border border-slate-200">
            {features.map((f, i) => {
              const Icon = f.icon;
              return (
                <div key={i} className="bg-white p-8 hover:bg-slate-50 transition-colors group" data-testid={`feature-${i}`}>
                  <div className="flex items-center gap-3 mb-4">
                    <div className="w-9 h-9 bg-[#0F172A] flex items-center justify-center group-hover:bg-[#2563EB] transition-colors">
                      <Icon className="w-4 h-4 text-white" />
                    </div>
                    <div className="font-mono text-xs text-slate-400">/{String(i + 1).padStart(2, "0")}</div>
                  </div>
                  <h3 className="font-display text-xl mb-2 tracking-tight">{f.title}</h3>
                  <p className="text-sm text-slate-600 leading-relaxed">{f.desc}</p>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Pricing */}
      <section id="pricing" className="bg-[#0F172A] text-white py-24 lg:py-32">
        <div className="max-w-5xl mx-auto px-6 lg:px-12">
          <div className="text-center mb-16">
            <div className="text-xs tracking-[0.3em] uppercase text-slate-400 mb-4">Tarification</div>
            <h2 className="font-display text-4xl sm:text-5xl tracking-tight">Un seul plan. Tout inclus.</h2>
            <p className="text-slate-400 mt-4 max-w-xl mx-auto">Sans piège, sans paliers cachés. Les 10 fonctionnalités, l'IA multi-modèles, et le support.</p>
          </div>

          <div className="grid md:grid-cols-2 gap-px bg-slate-800 border border-slate-800">
            <div className="bg-[#0F172A] p-10">
              <div className="text-xs tracking-[0.2em] uppercase text-slate-400">Essai</div>
              <div className="mt-4 font-display text-5xl tracking-tight">Gratuit</div>
              <div className="text-slate-400 mt-2">7 jours · sans CB</div>
              <ul className="mt-8 space-y-3 text-sm text-slate-300">
                <li className="flex gap-2"><Check className="w-4 h-4 text-emerald-400 shrink-0 mt-0.5" /> Toutes les fonctionnalités</li>
                <li className="flex gap-2"><Check className="w-4 h-4 text-emerald-400 shrink-0 mt-0.5" /> Jusqu'à 20 candidats</li>
                <li className="flex gap-2"><Check className="w-4 h-4 text-emerald-400 shrink-0 mt-0.5" /> 2 postes ouverts</li>
              </ul>
              <Link to="/signup" className="block mt-10" data-testid="pricing-trial-cta">
                <Button variant="outline" className="w-full border-slate-700 text-slate-900 bg-white hover:bg-slate-100 btn-pill h-12">Commencer l'essai</Button>
              </Link>
            </div>

            <div className="bg-white text-slate-900 p-10 relative">
              <div className="absolute top-0 right-0 bg-[#2563EB] text-white text-xs tracking-widest uppercase px-3 py-1.5">Pro</div>
              <div className="text-xs tracking-[0.2em] uppercase text-slate-500">Plan Pro</div>
              <div className="mt-4 font-display text-5xl tracking-tight">$50<span className="text-2xl text-slate-400">/mois</span></div>
              <div className="text-slate-500 mt-2">Annulable à tout moment</div>
              <ul className="mt-8 space-y-3 text-sm text-slate-700">
                <li className="flex gap-2"><Check className="w-4 h-4 text-emerald-600 shrink-0 mt-0.5" /> Candidats illimités</li>
                <li className="flex gap-2"><Check className="w-4 h-4 text-emerald-600 shrink-0 mt-0.5" /> Postes illimités</li>
                <li className="flex gap-2"><Check className="w-4 h-4 text-emerald-600 shrink-0 mt-0.5" /> Analyse IA + plan entretien</li>
                <li className="flex gap-2"><Check className="w-4 h-4 text-emerald-600 shrink-0 mt-0.5" /> Post-mortem & analytics</li>
                <li className="flex gap-2"><Check className="w-4 h-4 text-emerald-600 shrink-0 mt-0.5" /> Support prioritaire</li>
              </ul>
              <Link to="/signup" className="block mt-10" data-testid="pricing-pro-cta">
                <Button className="w-full bg-[#2563EB] hover:bg-[#1D4ED8] text-white btn-pill h-12">Souscrire</Button>
              </Link>
            </div>
          </div>
        </div>
      </section>

      <footer className="border-t border-slate-200 py-10">
        <div className="max-w-7xl mx-auto px-6 lg:px-12 flex flex-wrap items-center justify-between gap-4 text-sm text-slate-500">
          <div className="font-display tracking-tight">Xpert Recruit AI © 2026</div>
          <div>Construit pour les recruteurs qui doivent expliquer.</div>
        </div>
      </footer>
    </div>
  );
}
